import { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ToastContainer from '@/components/shared/ToastContainer';
import ChatBubble from '@/components/shared/ChatBubble';
import ScanReceiptModal from '@/components/shared/ScanReceiptModal';
import { useStore } from '@/utils/store';
import { useOnboarding } from '@/hooks/useOnboarding';
import Onboarding from '@/pages/onboarding/Onboarding';
import {
  Camera,
  LayoutDashboard,
  ArrowLeftRight,
  PieChart,
  CreditCard,
  Target,
  Users,
  Settings,
  Banknote,
  Radar,
  Briefcase,
  Building2,
  Hammer,
  CalendarRange,
  MoreHorizontal,
  X,
} from 'lucide-react';

// Desktop sidebar sections
const SIDEBAR_SECTIONS = [
  {
    items: [
      { key: 'xray', path: '/app', icon: Radar, end: true },
      { key: 'dashboard', path: '/app/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'nav.sectionFinances',
    items: [
      { key: 'transactions', path: '/app/transactions', icon: ArrowLeftRight },
      { key: 'incomeUnits', path: '/app/income-units', icon: Banknote },
      { key: 'budgets', path: '/app/budgets', icon: PieChart },
      { key: 'personalProjects', path: '/app/personal-projects', icon: Hammer },
    ],
  },
  {
    label: 'nav.sectionManagement',
    items: [
      { key: 'agenda', path: '/app/agenda', icon: CalendarRange },
      { key: 'credits', path: '/app/credits', icon: CreditCard },
      { key: 'goals', path: '/app/goals', icon: Target },
    ],
  },
  {
    label: 'nav.sectionPeople',
    items: [
      { key: 'family', path: '/app/family', icon: Users },
      { key: 'business', path: '/app/business', icon: Briefcase },
      { key: 'properties', path: '/app/properties', icon: Building2 },
    ],
  },
];

const SETTINGS_ITEM = { key: 'settings', path: '/app/settings', icon: Settings };

// Mobile: 4 primary tabs + "More"
const MOBILE_PRIMARY = [
  { key: 'xray', path: '/app', icon: Radar, end: true },
  { key: 'dashboard', path: '/app/dashboard', icon: LayoutDashboard },
  { key: 'transactions', path: '/app/transactions', icon: ArrowLeftRight },
  { key: 'budgets', path: '/app/budgets', icon: PieChart },
];

const MOBILE_MORE = [
  { key: 'incomeUnits', path: '/app/income-units', icon: Banknote },
  { key: 'personalProjects', path: '/app/personal-projects', icon: Hammer },
  { key: 'agenda', path: '/app/agenda', icon: CalendarRange },
  { key: 'credits', path: '/app/credits', icon: CreditCard },
  { key: 'goals', path: '/app/goals', icon: Target },
  { key: 'family', path: '/app/family', icon: Users },
  { key: 'business', path: '/app/business', icon: Briefcase },
  { key: 'properties', path: '/app/properties', icon: Building2 },
  { key: 'settings', path: '/app/settings', icon: Settings },
];

export default function AppLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { shouldShowOnboarding, completeOnboarding } = useOnboarding();
  const [scanOpen, setScanOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const chatEnabled = useStore((s) => s.appSettings?.chatEnabled !== false);
  const scanEnabled = useStore((s) => s.appSettings?.scanEnabled !== false);
  const businessEnabled = useStore((s) => s.appSettings?.businessEnabled === true);
  const propertiesEnabled = useStore((s) => s.appSettings?.propertiesEnabled === true);

  const filterNav = (items) => items.filter((item) => {
    if (item.key === 'business' && !businessEnabled) return false;
    if (item.key === 'properties' && !propertiesEnabled) return false;
    return true;
  });

  // Check if active page is in "More" menu
  const moreItems = filterNav(MOBILE_MORE);
  const isMoreActive = moreItems.some(({ path, end }) =>
    end ? location.pathname === path : location.pathname.startsWith(path)
  );

  // Find active key across all items for mobile label
  const allItems = [...MOBILE_PRIMARY, ...moreItems];
  const activeKey = allItems.find(({ path, end }) =>
    end ? location.pathname === path : location.pathname.startsWith(path)
  )?.key;

  if (shouldShowOnboarding) {
    return <Onboarding onComplete={completeOnboarding} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <ToastContainer />
      {chatEnabled && <ChatBubble />}
      {/* Floating scan button */}
      {scanEnabled && (
        <button
          onClick={() => setScanOpen(true)}
          className={`fixed right-4 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center ${
            chatEnabled ? 'bottom-[7rem] md:bottom-[5.5rem]' : 'bottom-24 md:bottom-6'
          }`}
          aria-label={t('scan.scanReceipt')}
        >
          <Camera size={22} />
        </button>
      )}
      <ScanReceiptModal open={scanOpen} onClose={() => setScanOpen(false)} />

      {/* Main content */}
      <main className="pb-20 md:pb-0 md:ml-64">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Outlet />
        </div>
      </main>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex-col p-4">
        <div className="flex items-center gap-2 px-3 py-4 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center text-white font-bold text-sm">
            K2
          </div>
          <span className="font-display font-bold text-lg text-gray-900 dark:text-white">
            Kuadra2
          </span>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto">
          {SIDEBAR_SECTIONS.map((section, si) => {
            const items = filterNav(section.items);
            if (items.length === 0) return null;
            return (
              <div key={si} className={si > 0 ? 'pt-4' : ''}>
                {section.label && (
                  <div className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    {t(section.label)}
                  </div>
                )}
                <div className="space-y-0.5">
                  {items.map(({ key, path, icon: Icon, end }) => (
                    <NavLink
                      key={key}
                      to={path}
                      end={end}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`
                      }
                    >
                      <Icon size={18} />
                      {t(`nav.${key}`)}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        {/* Settings at bottom */}
        <div className="border-t border-gray-100 dark:border-gray-800 pt-2 mt-2">
          <NavLink
            to={SETTINGS_ITEM.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`
            }
          >
            <Settings size={18} />
            {t('nav.settings')}
          </NavLink>
        </div>
      </aside>

      {/* Mobile bottom nav — 4 primary + "More" */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-1 py-1 z-50">
        <div className="flex justify-around">
          {MOBILE_PRIMARY.map(({ key, path, icon: Icon, end }) => (
            <NavLink
              key={key}
              to={path}
              end={end}
              aria-label={t(`nav.${key}`)}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-2 text-[10px] transition-colors ${
                  isActive
                    ? 'text-brand-600 dark:text-brand-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`
              }
            >
              <Icon size={20} />
              {key === activeKey && (
                <span className="truncate max-w-[52px]">{t(`nav.${key}`)}</span>
              )}
            </NavLink>
          ))}
          {/* More button */}
          <button
            onClick={() => setMoreOpen(true)}
            aria-label={t('nav.more')}
            className={`flex flex-col items-center gap-0.5 px-2 py-2 text-[10px] transition-colors ${
              isMoreActive
                ? 'text-brand-600 dark:text-brand-400'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            <MoreHorizontal size={20} />
            {isMoreActive && activeKey ? (
              <span className="truncate max-w-[52px]">{t(`nav.${activeKey}`)}</span>
            ) : (
              <span>{t('nav.more')}</span>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile "More" sheet */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-3xl p-4 pb-8 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{t('nav.more')}</span>
              <button
                onClick={() => setMoreOpen(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {moreItems.map(({ key, path, icon: Icon }) => {
                const isActive = location.pathname.startsWith(path);
                return (
                  <button
                    key={key}
                    onClick={() => { setMoreOpen(false); navigate(path); }}
                    className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-colors ${
                      isActive
                        ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon size={22} />
                    <span className="text-xs font-medium">{t(`nav.${key}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
